import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/authContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  LayoutDashboard, 
  Users, 
  Package, 
  FileText, 
  BarChart3, 
  Settings, 
  Menu, 
  LogOut, 
  Building2,
  Wallet
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const response = await api.get('/api/business');
      setBusinesses(response.data.businesses);
      
      const savedBusinessId = localStorage.getItem('currentBusiness');
      if (savedBusinessId) {
        const business = response.data.businesses.find(b => b.id === savedBusinessId);
        if (business) {
          setCurrentBusiness(business);
          return;
        }
      }
      
      if (response.data.businesses.length > 0) {
        setCurrentBusiness(response.data.businesses[0]);
        localStorage.setItem('currentBusiness', response.data.businesses[0].id);
      } else {
        navigate('/business-setup');
      }
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    }
  };

  const handleBusinessChange = (businessId) => {
    const business = businesses.find(b => b.id === businessId);
    setCurrentBusiness(business);
    localStorage.setItem('currentBusiness', businessId);
    window.location.reload();
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Users, label: 'Customers', path: '/customers' },
    { icon: Package, label: 'Items', path: '/items' },
    { icon: FileText, label: 'Invoices', path: '/invoices' },
    { icon: Wallet, label: 'Payments', path: '/payments' },
    { icon: BarChart3, label: 'Reports', path: '/reports' },
    { icon: Settings, label: 'Settings', path: '/settings' }
  ];

  const NavLinks = () => (
    <>
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
              isActive
                ? 'bg-teal-700 text-white shadow-md'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            data-testid={`nav-${item.label.toLowerCase()}`}
          >
            <Icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <div className="p-6">
                <h2 className="text-xl font-bold font-heading text-teal-700">BillBook Pro</h2>
              </div>
              <nav className="px-3 space-y-1">
                <NavLinks />
              </nav>
              <div className="p-4 border-t mt-auto">
                <Button
                  variant="ghost"
                  className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={logout}
                  data-testid="logout-button"
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  Logout
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <span className="font-heading font-bold text-teal-700">BillBook Pro</span>
        </div>
        {currentBusiness && (
          <div className="text-sm font-medium text-gray-600 truncate max-w-[150px]">
            {currentBusiness.name}
          </div>
        )}
      </div>

      <div className="flex">
        <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-white border-r border-gray-200 min-h-screen sticky top-0">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold font-heading text-teal-700">BillBook Pro</h1>
            {currentBusiness && (
              <div className="mt-4">
                <Select value={currentBusiness.id} onValueChange={handleBusinessChange}>
                  <SelectTrigger className="w-full" data-testid="business-selector">
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span className="truncate">{currentBusiness.name}</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {businesses.map((business) => (
                      <SelectItem key={business.id} value={business.id}>
                        {business.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <nav className="flex-1 px-3 py-4 space-y-1">
            <NavLinks />
          </nav>

          <div className="p-4 border-t border-gray-200">
            <div className="text-sm text-gray-600 mb-2">{user?.name}</div>
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={logout}
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </aside>

        <main className="flex-1 p-4 lg:p-8">
          {currentBusiness || location.pathname === '/business-setup' ? (
            <div data-testid="main-content">{children}</div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Business Found</h2>
              <p className="text-gray-600 mb-4">Create your first business to get started</p>
              <Button onClick={() => navigate('/business-setup')} data-testid="create-business-button">
                Create Business
              </Button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Layout;